import { useEffect, useState, useCallback } from 'react';
import { Table, Typography, Input, Select, Tag, Drawer, Button, message, Tooltip } from 'antd';
import { SearchOutlined, SendOutlined, ReloadOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';

const { Title, Text } = Typography;

const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-AR') : '—');

export const EmailLogs = () => {
  const { theme } = useClient();
  const primary = theme?.primaryColor || '#111827';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [estado, setEstado] = useState('');
  const [q, setQ] = useState('');
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [reenviando, setReenviando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await api.admin.emailLogs({ page, limit: 25, estado, q });
      if (res.status === 'success') {
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      }
    } finally {
      setCargando(false);
    }
  }, [page, estado, q]);

  useEffect(() => { cargar(); }, [cargar]);

  // El HTML completo se pide aparte: el listado no lo trae para no cargar
  // miles de cuerpos que nadie va a mirar.
  const abrir = async (id) => {
    const res = await api.admin.emailLog(id);
    if (res.status === 'success') setDetalle(res.data);
  };

  const reenviar = async () => {
    setReenviando(true);
    try {
      const res = await api.admin.reenviarEmail(detalle.id);
      if (res.status === 'success') {
        message.success('Reenviado');
        cargar();
      } else {
        message.error(res.message || 'No se pudo reenviar');
      }
    } finally {
      setReenviando(false);
    }
  };

  const columnas = [
    { title: 'Fecha', dataIndex: 'enviado_en', width: 165, render: fecha },
    { title: 'Tipo', dataIndex: 'template', width: 140 },
    { title: 'Para', dataIndex: 'destinatario', ellipsis: true },
    { title: 'Asunto', dataIndex: 'asunto', ellipsis: true },
    {
      title: 'Estado', dataIndex: 'estado', width: 110,
      render: (v, r) => (
        <Tooltip title={r.error || ''}>
          <Tag color={v === 'enviado' ? 'success' : 'error'}>{v}</Tag>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Mails enviados</Title>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Input
          placeholder="Buscar por destinatario o asunto"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 300 }}
          onPressEnter={(e) => { setPage(1); setQ(e.target.value); }}
          onChange={(e) => { if (!e.target.value) { setPage(1); setQ(''); } }}
        />
        <Select
          placeholder="Estado"
          allowClear
          style={{ width: 150 }}
          value={estado || undefined}
          onChange={(v) => { setPage(1); setEstado(v || ''); }}
          options={[
            { value: 'enviado', label: 'Enviado' },
            { value: 'error', label: 'Con error' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={cargar}>Actualizar</Button>
      </div>

      <Table
        size="small"
        rowKey="id"
        loading={cargando}
        columns={columnas}
        dataSource={items}
        onRow={(r) => ({ onClick: () => abrir(r.id), style: { cursor: 'pointer' } })}
        pagination={{
          current: page, pageSize: 25, total,
          onChange: setPage, showSizeChanger: false,
        }}
        locale={{ emptyText: 'Todavía no salió ningún mail' }}
      />

      <Drawer
        open={!!detalle}
        onClose={() => setDetalle(null)}
        width={720}
        title={detalle?.asunto}
        extra={
          <Button
            type="primary" icon={<SendOutlined />} loading={reenviando}
            onClick={reenviar}
            style={{ background: primary, borderColor: primary }}
          >
            Reenviar
          </Button>
        }
      >
        {detalle && (
          <>
            <div style={{ marginBottom: 12, fontSize: 13 }}>
              <div><Text type="secondary">Para: </Text>{detalle.destinatario}</div>
              <div><Text type="secondary">De: </Text>{detalle.remitente}</div>
              <div><Text type="secondary">Enviado: </Text>{fecha(detalle.enviado_en)}</div>
              {detalle.error && <div style={{ color: '#ef4444', marginTop: 6 }}>{detalle.error}</div>}
            </div>
            {/* srcDoc y no dangerouslySetInnerHTML: el mail se ve aislado del
                CSS del admin, que es como lo ve el cliente en su casilla. */}
            <iframe
              title="mail"
              srcDoc={detalle.cuerpo_html}
              style={{ width: '100%', height: '70vh', border: '1px solid #e5e7eb', borderRadius: 8 }}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};
